import { useStableKeys } from '../../hooks/useStableKeys';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XIcon } from 'lucide-react';
import { undoToast } from './undoToast';
import { Field } from './Field';
import { ImageField } from './ImageField';
import { MediaField } from './MediaField';
import { isMediaShape } from '../../core/rules/media';
import { NumberInput } from './NumberInput';
import { useAdmin } from '../AdminContext';
import { fieldLabels, humanize, imageKeys, selectOptions } from '../adminLabels';
import { btnIcon, inputCls } from './styles';
import { move } from '../editing';
import { fill } from '../../utils/copy';
export type Json = string | number | boolean | null | Json[] | {
  [key: string]: Json;
};
type Props = {
  value: Json;
  onChange: (value: Json) => void;
  fieldKey?: string;
  depth?: number;
};

/** Langer dan dit wordt een tekstvak in plaats van één regel. */
const LONG_TEXT = 80;
/** Ongeveer zoveel tekens per regel in een tekstvak; zo groeit het vak mee (3–8 regels). */
const CHARS_PER_ROW = 90;
const rowsFor = (text: string) => Math.min(8, Math.max(3, Math.ceil(text.length / CHARS_PER_ROW)));

/** Velden die de site weglaat als ze leeg zijn, met wat Sarah dan ziet. */
const EMPTY_HIDES: Record<string, string> = {
  cta: 'Leeg: deze knop staat niet op de site.',
  secondaryCta: 'Leeg: deze knop staat niet op de site.',
  eyebrow: 'Leeg: er staat geen regel boven de kop.',
  body: 'Leeg: er staat geen tekst onder de kop.'
};
/** Gedachtestreepjes: de huisstijl gebruikt een punt, komma of dubbele punt. */
const DASH = /\s[—–]\s|—/;
const isObject = (v: Json): v is {
  [key: string]: Json;
} => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Een leeg exemplaar met dezelfde vorm — voor "toevoegen" in een lijst. */
const emptied = (v: Json): Json => {
  if (typeof v === 'string') return '';
  if (typeof v === 'number') return 0;
  if (typeof v === 'boolean') return false;
  if (Array.isArray(v)) return [];
  if (isObject(v)) return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, emptied(x)]));
  return null;
};

/** Eerste stukje tekst uit een item, als titel van de kaart. */
const titleOf = (v: Json): string => {
  if (typeof v === 'string') return v;
  if (isObject(v)) {
    for (const key of ['title', 'name', 'text', 'code', 'quote', 'label']) {
      const x = v[key];
      if (typeof x === 'string' && x) return x;
    }
  }
  return '';
};

/**
 * Bouwt automatisch een formulier op uit de vorm van de data.
 * Zo is elke tekst op de site te bewerken zonder dat er per blok een scherm
 * gebouwd hoeft te worden — en een nieuw tekstveld in data/pages.ts
 * verschijnt hier vanzelf.
 */
/**
 * Geneste groepen zonder kaders: een zachte vlakkleur die per laag wisselt (wit paneel → zand →
 * wit → …), zodat je ziet wat bij elkaar hoort zonder doos-in-doos.
 */
const tint = (depth: number) => depth % 2 === 1 ? 'bg-shell' : 'bg-white';
export function ContentEditor({
  value,
  onChange,
  fieldKey,
  depth = 0
}: Props) {
  const meta = fieldKey ? fieldLabels[fieldKey] : undefined;
  if (meta?.hidden) return null;
  const label = fieldKey ? humanize(fieldKey) : '';
  if (typeof value === 'string') {
    return <TextField fieldKey={fieldKey ?? ''} label={label} value={value} onChange={onChange} data-id="element-4917" data-mp="j61jgoiyofi7" />;
  }
  if (typeof value === 'number') {
    return <Field label={label} hint={meta?.hint} data-id="element-4918" data-mp="9gi07hrc6jmt">
        <NumberInput decimals value={Number.isFinite(value) ? value : 0} onChange={v => v !== undefined && onChange(v)} className="max-w-[12rem]" data-id="element-4919" data-mp="9fgl92779czt" />
      </Field>;
  }
  if (typeof value === 'boolean') {
    return <label className="flex min-h-11 items-center gap-3 text-[15px] text-ink sm:min-h-0 sm:gap-2 sm:text-[14px]" data-id="element-4920" data-mp="3vlwson11va2e">
        <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="h-5 w-5 shrink-0 accent-clay sm:h-4 sm:w-4" data-id="element-4921" data-mp="3mnjnekvm52dc" />
        {label}
      </label>;
  }
  if (Array.isArray(value)) {
    return <ListField fieldKey={fieldKey ?? ''} label={label} value={value} onChange={onChange} depth={depth} data-id="element-4922" data-mp="1kwggo6oug7y2" />;
  }

  // Een beeld ({ image, alt, focus?, video? }): één veld met uitsnede en video, niet vier losse.
  if (isMediaShape(value)) {
    return <MediaField label={label || 'Beeld'} hint={meta?.hint} value={value} onChange={next => onChange(next as unknown as Json)} data-id="element-4923" data-mp="3c11klhhpp4dm" />;
  }
  if (isObject(value)) {
    const entries = Object.entries(value).filter(([k]) => !fieldLabels[k]?.hidden);
    const body = <div className="space-y-5" data-id="element-4924" data-mp="2y1il1d2yv1cx">
        {entries.map(([k, v]) => <ContentEditor key={k} fieldKey={k} value={v} depth={depth + 1} onChange={next => onChange({
        ...value,
        [k]: next
      })} data-id="element-4925" data-mp="2ta0k2p0y10ry" />)}
      </div>;
    if (!fieldKey || depth === 0) return body;
    return <fieldset className={`rounded-md p-4 ${tint(depth)}`} data-id="element-4926" data-mp="2k9ve8jbeeafw">
        <legend className="px-1.5 text-[13px] font-medium text-ink" data-id="element-4927" data-mp="2k799erfw691d">{label}</legend>
        {meta?.hint && <p className="-mt-1 mb-3 text-[12px] text-muted" data-id="element-4928" data-mp="2fbtxw65cwp84">{meta.hint}</p>}
        {body}
      </fieldset>;
  }
  return null;
}
function TextField({
  fieldKey,
  label,
  value,
  onChange
}: {
  fieldKey: string;
  label: string;
  value: string;
  onChange: (v: Json) => void;
}) {
  const {
    catalog
  } = useAdmin();
  const meta = fieldLabels[fieldKey];
  const options = selectOptions[fieldKey];
  if (imageKeys.has(fieldKey)) {
    return <Field label={label} hint={meta?.hint} as="div" data-id="element-4929" data-mp="13swnt542plqm">
        <div className="max-w-[14rem]" data-id="element-4930" data-mp="13qiryu1kk5i0">
          <ImageField value={value} onChange={onChange} fit="cover" aspect="aspect-[4/3]" data-id="element-4931" data-mp="72vzt8bj39ad" />
        </div>
      </Field>;
  }
  if (options) {
    return <Field label={label} hint={meta?.hint} data-id="element-4932" data-mp="bzcmdy32xflc">
        <select value={value} onChange={e => onChange(e.target.value)} className={`${inputCls} max-w-xs`} data-id="element-4933" data-mp="g2shur3ex9wg">
          {options.map(o => <option key={o.value} value={o.value} data-id="element-4934" data-mp="kvvg3cj4x7iz">
              {o.label}
            </option>)}
        </select>
      </Field>;
  }
  if (fieldKey === 'pickId') {
    return <Field label={label} hint={meta?.hint} data-id="element-4935" data-mp="3oh18htmdjqlz">
        <select value={value} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-4936" data-mp="vnu6alva71ho">
          {!catalog.products.some(p => p.id === value) && <option value={value} data-id="element-4937" data-mp="qp0sesc318gz">Onbekend: {value}</option>}
          {catalog.products.map(p => <option key={p.id} value={p.id} data-id="element-4938" data-mp="15ddqpvyn73en">
              {p.brand} · {p.name}
            </option>)}
        </select>
      </Field>;
  }
  const long = meta?.long || value.length > LONG_TEXT;
  const filled = value.includes('{') ? fill(value) : null;
  return <Field label={label} hint={meta?.hint} data-id="element-4939" data-mp="7rgc49ndvi7z">
      {long ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={rowsFor(value)} className={`${inputCls} resize-y leading-relaxed`} data-id="element-4940" data-mp="lpxzg69dmspd" /> : <input value={value} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-4941" data-mp="hnb2jqxumhlb" />}
      {filled && filled !== value && <span className="mt-1.5 block text-[12px] leading-snug text-muted" data-id="element-4942" data-mp="1t4asifwq4kc6">
          <span className="text-sage" data-id="element-4943" data-mp="1t64489m71ur1">Op de site:</span> {filled}
        </span>}
      {!value.trim() && EMPTY_HIDES[fieldKey] && <span className="mt-1.5 block text-[12px] text-muted" data-id="element-4944" data-mp="25mwr30jkq26z">{EMPTY_HIDES[fieldKey]}</span>}
      {DASH.test(value) && <span className="mt-1.5 block text-[12px] text-muted" data-id="element-4945" data-mp="1qqebl34fww22">Tip: een punt, komma of dubbele punt leest rustiger dan een gedachtestreepje.</span>}
    </Field>;
}
function ListField({
  fieldKey,
  label,
  value,
  onChange,
  depth
}: {
  fieldKey: string;
  label: string;
  value: Json[];
  onChange: (v: Json) => void;
  depth: number;
}) {
  const meta = fieldLabels[fieldKey];
  const simple = value.every(v => typeof v === 'string');
  const template: Json = value.length ? emptied(value[0]) : '';
  const set = (i: number, next: Json) => onChange(value.map((v, j) => j === i ? next : v));

  // Vaste sleutels per rij (los van de positie), zodat focus en invoer bij de juiste rij
  // blijven na verplaatsen of verwijderen. Houdt de lengte gelijk als de lijst van buitenaf wijzigt.
  const rows = useStableKeys(value.length);
  const reorder = (from: number, to: number) => {
    rows.move(from, to);
    onChange(move(value, from, to));
  };
  const remove = (i: number) => {
    const before = value;
    const beforeIds = rows.snapshot();
    const name = titleOf(value[i]);
    rows.remove(i);
    onChange(value.filter((_, j) => j !== i));
    // Eén klik op het kruisje wist soms een heel citaat of een aflevering: altijd terug te zetten.
    undoToast(name ? `“${name.slice(0, 40)}${name.length > 40 ? '…' : ''}” verwijderd` : 'Verwijderd', () => {
      rows.restore(beforeIds);
      onChange(before);
    });
  };
  const controls = (i: number) => {
    // Schermlezers horen welke regel het is: "Veld 2 van 5 omhoog".
    const name = `${label || 'Regel'} ${i + 1} van ${value.length}`;
    return <span className="flex shrink-0 items-center justify-end" data-id="element-4946" data-mp="5hrevcdrcots">
        <button type="button" onClick={() => reorder(i, i - 1)} disabled={i === 0} aria-label={`${name} omhoog`} className={btnIcon} data-id="element-4947" data-mp="k9i0kd0m025">
          <ArrowUpIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4948" data-mp="3mkni53kq1v8j" />
        </button>
        <button type="button" onClick={() => reorder(i, i + 1)} disabled={i === value.length - 1} aria-label={`${name} omlaag`} className={btnIcon} data-id="element-4949" data-mp="3rj8hz1w2acwg">
          <ArrowDownIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4950" data-mp="571vaa2ncmg8" />
        </button>
        <button type="button" onClick={() => remove(i)} aria-label={`${name} verwijderen`} className={btnIcon} data-id="element-4951" data-mp="96sik3syskg0">
          <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4952" data-mp="e53tyiywr30c" />
        </button>
      </span>;
  };
  return <Field label={label} hint={meta?.hint} as="div" data-id="element-4953" data-mp="j3x90ftuf22m">
      {simple ? <ul className="space-y-2" data-id="element-4954" data-mp="o4cn59ly8dln">
          {value.map((v, i) => <li key={rows.keys[i]} className="flex flex-col gap-1 sm:flex-row sm:items-start" data-id="element-4955" data-mp="2ky6aaos0fql0" data-mpi={i}>
              {meta?.long || String(v).length > LONG_TEXT ? <textarea value={String(v)} onChange={e => set(i, e.target.value)} rows={rowsFor(String(v))} aria-label={`${label} ${i + 1}`} className={`${inputCls} resize-y leading-relaxed`} data-id="element-4956" data-mp="gfjncuwj7llv" /> : <input value={String(v)} onChange={e => set(i, e.target.value)} aria-label={`${label} ${i + 1}`} className={inputCls} data-id="element-4957" data-mp="ufug63heja9o" />}
              {controls(i)}
            </li>)}
        </ul> : <ul className="space-y-3" data-id="element-4958" data-mp="14c7gvlbd6odx">
          {value.map((v, i) => <li key={rows.keys[i]} className={`rounded-md ${tint(depth + 1)}`} data-id="element-4959" data-mp="193yzn9oa3pti" data-mpi={i}>
              <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2" data-id="element-4960" data-mp="1d5klvlmpdggd">
                <span className="min-w-0 truncate text-[13px] text-muted" data-id="element-4961" data-mp="1i55gxkyr54nt">
                  <span className="text-ink" data-id="element-4962" data-mp="1i32l2kax4hb6">{i + 1}.</span> {titleOf(v) || 'Nieuw'}
                </span>
                {controls(i)}
              </div>
              <div className="p-4" data-id="element-4963" data-mp="1x8q758aw6pjj">
                <ContentEditor value={v} onChange={next => set(i, next)} depth={depth + 1} data-id="element-4964" data-mp="1x5ub5piluaxq" />
              </div>
            </li>)}
        </ul>}
      <button type="button" onClick={() => onChange([...value, template])} className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-2.5 text-[14px] text-clay transition-colors duration-150 ease-soft hover:bg-sand sm:py-1.5 sm:text-[13px]" data-id="element-4965" data-mp="1naisiw42sjkp">
        <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4966" data-mp="2az21lduy90du" />
        Toevoegen
        {label && <span className="sr-only" data-id="element-4967" data-mp="267jzunl3qvrt">: {label}</span>}
      </button>
    </Field>;
}